import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';

/**
 * Sign in, and — since 2026-09-28 — forgot password. "Forgot password?" asks for an email and
 * the server mails a one-time link (30 minutes) to https://…/?reset=<token>; opening that link
 * lands here in the "choose a new password" step. The app has no router, so the link is a query
 * parameter read once, and removed from the address bar as soon as it has been used or left.
 */
type Mode = 'login' | 'forgot' | 'forgotSent' | 'reset' | 'resetDone';

const readResetToken = (): string | null => {
  try { return new URLSearchParams(window.location.search).get('reset'); } catch { return null; }
};

const dropResetTokenFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('reset')) return;
    url.searchParams.delete('reset');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  } catch { /* the address bar is cosmetic here */ }
};

const LoginPage: React.FC = () => {
  const [resetToken, setResetToken] = useState<string | null>(readResetToken);
  const [mode, setMode] = useState<Mode>(() => (resetToken ? 'reset' : 'login'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [linkExpired, setLinkExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const { t, isRTL } = useTranslation();

  const goTo = (next: Mode) => {
    setError('');
    setLinkExpired(false);
    if (next !== 'reset') {
      dropResetTokenFromUrl();
      setResetToken(null);
    }
    setMode(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!email || !password) {
      setError(t('pleaseEnterBoth'));
      return;
    }

    setIsLoading(true);

    try {
      await login(email, password);
      // AuthContext will handle redirecting to main app
    } catch (err: any) {
      setError(err.message || t('loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError(t('enterValidEmail'));
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (response.ok) setMode('forgotSent');
      else if (response.status === 503) setError(t('resetUnavailable'));
      else if (response.status === 429) setError(t('tooManyRequests'));
      else if (response.status === 400) setError(t('enterValidEmail'));
      else setError(t('resetFailed'));
    } catch {
      setError(t('resetFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLinkExpired(false);
    if (newPassword.length < 8) {
      setError(t('passwordMinLength'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('passwordsDoNotMatch'));
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, password: newPassword }),
      });
      if (response.ok) {
        dropResetTokenFromUrl();
        setResetToken(null);
        setNewPassword('');
        setConfirmPassword('');
        setPassword('');
        setMode('resetDone');
      } else if (response.status === 429) {
        setError(t('tooManyRequests'));
      } else if (response.status === 400) {
        const body = await response.json().catch(() => ({}));
        if (/at least 8/.test(String(body?.error || ''))) {
          setError(t('passwordMinLength'));
        } else {
          setError(t('resetLinkInvalid'));
          setLinkExpired(true);
        }
      } else {
        setError(t('resetFailed'));
      }
    } catch {
      setError(t('resetFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const title = mode === 'forgot' || mode === 'forgotSent'
    ? t('forgotTitle')
    : mode === 'reset' ? t('newPasswordTitle') : t('loginTitle');
  const subtitle = mode === 'forgot' ? t('forgotSubtitle') : mode === 'login' ? t('loginSubtitle') : '';

  const spinnerLabel = (label: string) => (
    <>
      <span className="spinner-small"></span>
      <span>{label}</span>
    </>
  );

  return (
    <div className="login-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="login-container">
        <div className="login-card">
          {/* Logo */}
          <div className="login-logo">
            <img
              src="https://mabat3d.com/_next/static/media/logo.ed3d26f6.svg"
              alt="Mabat3D Logo"
              style={{ height: '48px' }}
            />
          </div>

          {/* Title */}
          <h1 className="login-title">{title}</h1>
          {subtitle && <p className="login-subtitle">{subtitle}</p>}

          {/* Error message */}
          {error && (
            <div className="login-error" role="alert">
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {mode === 'login' && (
            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  {t('emailAddress')}
                </label>
                <input
                  id="email"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('enterEmail')}
                  className="form-input"
                  autoComplete="email"
                  autoFocus
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  {t('password')}
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('enterPassword')}
                  className="form-input"
                  autoComplete="current-password"
                  disabled={isLoading}
                />
                <button type="button" className="login-link login-link--forgot" onClick={() => goTo('forgot')} disabled={isLoading}>
                  {t('forgotPassword')}
                </button>
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={isLoading}
              >
                {isLoading ? spinnerLabel(t('signingIn')) : t('signIn')}
              </button>
            </form>
          )}

          {mode === 'forgot' && (
            <form onSubmit={handleForgot} className="login-form">
              <div className="form-group">
                <label htmlFor="forgot-email" className="form-label">
                  {t('emailAddress')}
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('enterEmail')}
                  className="form-input"
                  autoComplete="email"
                  autoFocus
                  disabled={isLoading}
                />
              </div>
              <button type="submit" className="btn btn-primary btn-block" disabled={isLoading}>
                {isLoading ? spinnerLabel(t('sendingResetLink')) : t('sendResetLink')}
              </button>
              <button type="button" className="login-link login-link--back" onClick={() => goTo('login')} disabled={isLoading}>
                {t('backToSignIn')}
              </button>
            </form>
          )}

          {mode === 'forgotSent' && (
            <div className="login-form">
              <div className="login-notice" role="status">{t('forgotSent')}</div>
              <button type="button" className="btn btn-primary btn-block" onClick={() => goTo('login')}>
                {t('backToSignIn')}
              </button>
            </div>
          )}

          {mode === 'reset' && (
            <form onSubmit={handleReset} className="login-form">
              <div className="form-group">
                <label htmlFor="new-password" className="form-label">
                  {t('newPassword')}
                </label>
                <input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('enterNewPassword')}
                  className="form-input"
                  autoComplete="new-password"
                  autoFocus
                  disabled={isLoading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirm-password" className="form-label">
                  {t('confirmNewPassword')}
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('reenterNewPassword')}
                  className="form-input"
                  autoComplete="new-password"
                  disabled={isLoading}
                />
              </div>
              <button type="submit" className="btn btn-primary btn-block" disabled={isLoading}>
                {isLoading ? spinnerLabel(t('savingPassword')) : t('savePassword')}
              </button>
              {linkExpired ? (
                <button type="button" className="login-link login-link--back" onClick={() => goTo('forgot')}>
                  {t('requestNewLink')}
                </button>
              ) : (
                <button type="button" className="login-link login-link--back" onClick={() => goTo('login')} disabled={isLoading}>
                  {t('backToSignIn')}
                </button>
              )}
            </form>
          )}

          {mode === 'resetDone' && (
            <div className="login-form">
              <div className="login-notice" role="status">{t('passwordUpdated')}</div>
              <button type="button" className="btn btn-primary btn-block" onClick={() => goTo('login')}>
                {t('signIn')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
